import { ButtonBlinkMode, consumer, GameState, GameTime, I18NextProvider, IdleStatusProvider, inject } from "@spawn/core";
import { FreegameService } from "@spawn/freegames";
import { GameMessageService, GameMessageType } from "@spawn/modern-ui";
import { NudgingService } from "@spawn/nudge";
import { RevealService } from "@spawn/reveal";
import {
	AmbientSoundController,
	AmbientSounds,
	AutoplayController,
	AutoPlayEvent,
	CasinoOptionsService,
	CurrencyService,
	NetworkService,
	setActiveSymbolMetaDataEvent,
	SlotMachineButtonId,
	SpinController,
	stopAutoplayCounterEvent,
	SymbolMetaDataKeys,
	UiEvents,
} from "@spawn/slot-machine";
import { first } from "rxjs";
import { GameEvents } from "../events/events";
import { IGameNetworkState } from "../network/networkState";
import { NudgeUpService } from "../services/nudgeUpService";
import { ReelsetService } from "../services/reelsetService";
import { FreegameInitialSpinningState } from "./freegameInitialSpinningState";

@consumer
export class FreegameInitialNudgingState extends GameState {
	@inject private readonly nudgingService!: NudgingService;
	@inject private readonly nudgeUpService!: NudgeUpService;
	@inject private readonly gameTime!: GameTime;
	@inject private readonly freegameService!: FreegameService;
	@inject private readonly gameMessageService!: GameMessageService;
	@inject private readonly i18n!: I18NextProvider;
	@inject private readonly currencyService!: CurrencyService;
	@inject private readonly autoplayController!: AutoplayController;
	@inject private readonly idleStatusProvider!: IdleStatusProvider;
	@inject private readonly casinoOptionsService!: CasinoOptionsService;
	@inject private readonly networkService!: NetworkService<IGameNetworkState>;
	@inject private readonly spinController!: SpinController;
	@inject private readonly ambientSoundController!: AmbientSoundController;
	@inject private readonly reelsetService!: ReelsetService;
	@inject private readonly revealService!: RevealService;

	public override async onEnter(): Promise<void> {
		this.ambientSoundController.stopSound(AmbientSounds.BaseGame);
		this.ambientSoundController.playSound(AmbientSounds.SideGame);

		this.reelsetService.replaceVisibleSymbolsOnReelset(this.networkService.state.preNudgeUpReels!);

		// OS-21: make sure we revert the hideOffscreenMysterySymbols that might have hidden offscreen symbols in the triggering spin
		this.revealService.showOffscreenMysterySymbols();

		this.setGameMessage();
		this.teaseMysterySymbols();
		this.addButtonSubscriptions();
		this.setButtonStates();

		await this.gameTime.waitFor(0.5);

		this.eventDispatcher.dispatch({ type: GameEvents.SetMultiplier, payload: { multiplier: 1 } });

		await this.gameTime.waitFor(0.5);

		if (this.nudgeUpService.initialNudgeups > 0) {
			await this.nudgeUpService.performNudgesUp(this.nudgeUpService.initialNudgeups);
			this.nudgeUpService.initialNudgeups = 0;
			this.nudgingService.complete();
		}

		this.setPostNudgeButtonStates();
		if (this.autoplayController.isAutoSpinEnabled) {
			await this.gameTime.waitFor(this.spinController.autoSpinDelay());
			this.stateComplete(FreegameInitialSpinningState);
		}
	}

	// Taken as is from the FG IdleState
	private setGameMessage(): void {
		const { totalWinAmount } = this.freegameService.state!; // at this point in the game this total will also include any win from the fg preparation spin, that should not be displayed yet
		const { wonAmount } = this.networkService.state;

		this.gameMessageService.setCustomGameMessage(
			this.i18n.t("freegames.currentRound", {
				currentRound: 1,
				totalRounds: 1,
			}),
			GameMessageType.Primary
		);

		// Display the total won amount over the course of freegames in the second line.
		this.gameMessageService.setCustomGameMessage(
			this.i18n.t("general.totalWin", {
				amount: this.currencyService.getLocalized(totalWinAmount - wonAmount), // subtract the win from the spin that's about to happen
			}),
			GameMessageType.Secondary
		);
	}

	private teaseMysterySymbols(): void {
		const symbolCoordinates = new Array(5).fill(0).flatMap((_, x) => new Array(4).fill(0).map((_, y) => ({ x, y })));

		for (const coordinate of symbolCoordinates) {
			this.eventDispatcher.dispatch(
				setActiveSymbolMetaDataEvent({
					symbolCoordinate: coordinate,
					symbolMetaDataKeys: [SymbolMetaDataKeys.BaseTexture, "MysteryTeaserAnimation"],
				})
			);
		}
	}

	protected addButtonSubscriptions(): void {
		// Dispatch event to not update remaining spins during the free games.
		this.eventDispatcher.dispatch(stopAutoplayCounterEvent());
		this.subscriptions.add(
			this.eventDispatcher
				.event$(UiEvents.Spin)
				.pipe(first())
				.subscribe(() => this.stateComplete(FreegameInitialSpinningState))
		);
		this.subscriptions.add(
			this.eventDispatcher.event$<AutoPlayEvent>(UiEvents.AutoPlay).subscribe(({ payload }) => {
				if (payload.isActive === true) {
					this.autoplayController.isAutoSpinEnabled = true;
					this.stateComplete(FreegameInitialSpinningState);
				}
			})
		);
		this.subscriptions.add(
			this.idleStatusProvider.idle$(5).subscribe(() => {
				this.buttonStateController.updateState([SlotMachineButtonId.Spin], { blinkMode: ButtonBlinkMode.Blinking });
			})
		);
	}

	protected setButtonStates(): void {
		this.buttonStateController.updateState([SlotMachineButtonId.MaxBet, SlotMachineButtonId.Lines, SlotMachineButtonId.Bet], {
			isEnabled: false,
			isVisible: false,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.Spin], {
			isEnabled: false,
			isVisible: true,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.AutoSpin], {
			isVisible: this.networkService.isAutoPlayAllowed,
			isEnabled: false,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.TurboSpin], {
			isVisible: this.networkService.isQuickSpinAllowed,
			isEnabled: false,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.QuickMenu], {
			isVisible: true,
			isEnabled: !this.autoplayController.isAutoSpinEnabled,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.Sound], {
			isVisible: this.casinoOptionsService.options.showSoundButton,
			isEnabled: this.casinoOptionsService.options.showSoundButton,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.Help, SlotMachineButtonId.PayTable], {
			isVisible: this.casinoOptionsService.options.showOneClickButtons,
			isEnabled: !this.autoplayController.isAutoSpinEnabled && this.casinoOptionsService.options.showOneClickButtons,
		});

		this.buttonStateController.updateState([SlotMachineButtonId.Home], {
			isVisible: this.casinoOptionsService.options.showHomeButton,
			isEnabled: this.casinoOptionsService.options.showHomeButton,
		});
	}

	private setPostNudgeButtonStates(): void {
		this.buttonStateController.updateState([SlotMachineButtonId.Spin], {
			isEnabled: true,
		});
		this.buttonStateController.updateState([SlotMachineButtonId.AutoSpin], {
			isVisible: this.networkService.isAutoPlayAllowed,
			isEnabled: this.networkService.isAutoPlayAllowed,
		});
		this.buttonStateController.updateState([SlotMachineButtonId.TurboSpin], {
			isVisible: this.networkService.isQuickSpinAllowed,
			isEnabled: this.networkService.isQuickSpinAllowed && !this.autoplayController.isAutoSpinEnabled,
		});
	}
}
